import React, { useState, useEffect, useRef } from 'react';
import { sendClientMessage } from '../services/clientReporter';
import { HelpCircle, X } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * ReportIssueButton (Jose 2026-10-13; collapsible 2026-10-23) — portal "Need help?" launcher.
 * The full pill floated over the dashboard cards + bottom nav and got in the way, so it now DEFAULTS
 * to a small icon TUCKED against the right edge (out of the way). Tap it → it slides out into the
 * labeled "Need help?" pill; tap the pill → the report modal opens. The expanded pill auto-tucks back
 * after a few seconds (or when the modal closes) so it never blocks content. Message lands in
 * `clientMessages` with the session's recent errors attached for the shop to see + respond.
 */
const ReportIssueButton: React.FC = () => {
  const [open, setOpen] = useState(false);        // report modal
  const [expanded, setExpanded] = useState(false); // pill expanded vs tucked edge icon
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const tuckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-tuck the expanded pill back to the edge after a few seconds so it's never left in the way.
  useEffect(() => {
    if (!expanded) return;
    if (tuckTimer.current) clearTimeout(tuckTimer.current);
    tuckTimer.current = setTimeout(() => setExpanded(false), 4000);
    return () => { if (tuckTimer.current) clearTimeout(tuckTimer.current); };
  }, [expanded]);

  const submit = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try {
      await sendClientMessage(msg.trim());
      toast.success("Thanks — we got it! We'll take a look and follow up.");
      setMsg('');
      setOpen(false);
      setExpanded(false);
    } catch {
      toast.error('Couldn’t send that — please try again in a moment.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (expanded ? (
        // EXPANDED — the labeled pill; tapping it opens the report modal. Small × tucks it away now.
        <div className="fixed bottom-24 right-3 z-[85] flex items-center gap-1.5">
          <button
            onClick={() => setOpen(true)}
            className="bg-purple-600 text-white text-sm font-bold rounded-full shadow-lg pl-3.5 pr-4 py-2.5 flex items-center gap-1.5 hover:bg-purple-700 active:scale-95 transition-all"
          >
            <HelpCircle className="w-4 h-4" /> Need help?
          </button>
          <button
            onClick={() => setExpanded(false)}
            aria-label="Hide"
            className="bg-white/90 text-gray-500 rounded-full shadow p-1.5 active:scale-95"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        // COLLAPSED (default) — a small icon tucked against the right edge so it never covers content.
        <button
          onClick={() => setExpanded(true)}
          aria-label="Need help?"
          className="fixed bottom-24 right-0 z-[85] bg-purple-600/90 text-white rounded-l-full shadow-lg pl-3 pr-2 py-2.5 hover:bg-purple-700 active:scale-95 transition-all"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      ))}

      {open && (
        <div className="fixed inset-0 z-[95] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={() => !sending && setOpen(false)}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900">Tell us what's going on</h3>
            <p className="text-sm text-gray-500 mt-1">Hit a snag or have a question? Send it here and we'll take care of it.</p>
            <textarea
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              autoFocus
              rows={4}
              placeholder="What happened? (e.g. “I tried to claim my account and got an error”)"
              className="w-full mt-3 p-3 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none resize-none"
            />
            <div className="flex gap-2 mt-3">
              <button onClick={() => setOpen(false)} disabled={sending} className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-medium">Cancel</button>
              <button onClick={submit} disabled={sending || !msg.trim()} className="flex-1 py-2.5 rounded-xl bg-purple-600 text-white font-bold disabled:opacity-50">
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportIssueButton;
